import { useState } from 'react';
import { ListEnd, X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { Modal } from '../../../Modal';
import { setRecapVisibleFor } from '../../../../lib/recap-preference';
import { IconButton } from '../../ui/IconButton';
import { Typography } from '../../ui/Typography';
import styles from './RecapBanner.module.css';

/**
 * The conversation's rolling recap, directly above the composer — what this conversation has been about,
 * for someone returning to a thread they last touched days ago.
 *
 * The × hides the recap on THIS conversation, and the chat header's menu is how it comes back — a control
 * that can only be switched off is a one-way door. Settings keeps the master switch for someone who does
 * not want the line on any thread.
 *
 * Figma "Recap" (16489:33486): NOT a card — a titled block on the page ground, parted from the transcript
 * above by a hairline. A header row (`list-end` mark, the label, the ×) sits over the recap itself in
 * italic. The italic is what marks the text as the app talking about the conversation rather than another
 * message in it. Shared 1:1 with native `RecapBanner`.
 *
 * Clicking it opens the whole recap in the shared `Modal` (whose scrim already carries the DS blur), since
 * the text is clamped to four lines and the rest of it otherwise has nowhere to go. Mirrors native
 * `RecapOverlay`.
 */
export function RecapBanner({ recap, conversationId }: { recap: string; conversationId: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <section className={styles.banner} aria-label={t('chat.recap.label')}>
      <div className={styles.header}>
        <ListEnd size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        <Typography variant="h4" color="primary" component="span" className={styles.label}>
          {t('chat.recap.label')}
        </Typography>
        <IconButton
          size="small"
          variant="text"
          title={t('chat.recap.hide')}
          aria-label={t('chat.recap.hide')}
          onClick={() => setRecapVisibleFor(conversationId, false)}
        >
          <X size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
      </div>
      {/* The recap itself opens it; the × keeps its own meaning and is outside this control. */}
      <button type="button" className={styles.text} onClick={() => setExpanded(true)} title={t('chat.recap.expand')}>
        {recap}
      </button>
      <Modal open={expanded} onClose={() => setExpanded(false)} title={t('chat.recap.label')} size="minimum" maxHeightPx={650} scrollBody>
        <p className={styles.full}>{recap}</p>
      </Modal>
    </section>
  );
}
